import React from 'react';
import { useAddFavoriteMutation, useGetFavoritesQuery, useGetStorefrontQuery, useRemoveFavoriteMutation } from '../../Api/Api';
import { ISearchContext } from '../../Api/Models/Products';
import CAN, { ORDERS } from '../Auth/Permissions';

// Shopper favorites. Favorites are keyed by the shopper's email, so they only exist for a signed-in
// customer — a guest session (anonymous login) has no identity to hang them off, and the BFF rejects
// the call. `enabled` folds that together with the storefront's own toggle, and every surface hides
// its heart when it is false rather than showing a control that cannot work.
//
// The favorite ids are fetched ONCE per page and shared by every heart, rather than each product
// carrying its own state: a toggle then invalidates one small query instead of refetching products.
// (Products do carry an `isFavorite` flag for the first paint, before this query resolves.)
export function useFavorites() {
    const settings = useGetStorefrontQuery();
    // ORDERS is the app's existing "is a real signed-in customer" gate (the NavBar hangs the cart and
    // account menu off it); an anonymous session never carries it.
    const signedIn = CAN(ORDERS);
    const enabled = (settings.data?.enableProductFavorites ?? false) && signedIn;

    const favorites = useGetFavoritesQuery(undefined, { skip: !enabled });
    const [addFavorite] = useAddFavoriteMutation();
    const [removeFavorite] = useRemoveFavoriteMutation();

    const ids = React.useMemo(() => new Set(favorites.data ?? []), [favorites.data]);

    const toggle = React.useCallback(
        // source/searchContext: the surface (and, when relevant, the search criteria) the favorite
        // toggle happened from — captured onto the interest signal by the BFF.
        async (productId: string, currentlyFavorite: boolean, source?: string, searchContext?: ISearchContext) => {
            if (!enabled || !productId) return;
            if (currentlyFavorite) await removeFavorite({ productId, source, searchContext });
            else await addFavorite({ productId, source, searchContext });
        },
        [enabled, addFavorite, removeFavorite]
    );

    // Falls back to the product's own flag until the id set has loaded, so a favorited product does
    // not flash an empty heart on first paint.
    const isFavorite = React.useCallback(
        (productId?: string, fallback?: boolean) => {
            if (!productId) return false;
            if (favorites.data === undefined) return fallback ?? false;
            return ids.has(productId);
        },
        [ids, favorites.data]
    );

    return { enabled, isFavorite, toggle };
}
